import { useEffect, useRef, useState } from 'react';
import { useGameStore } from '@campus/core';
import { Button, Icon } from '../ds/index.js';
import {
  BACKGROUND_PHOTOS, backgroundPhotoUrl, clearCustomBackground, isVideoBackground, loadCustomBackground,
  onCustomBackgroundChange, saveCustomBackground, type VideoBackground,
} from '../../lib/videoBackgrounds.js';

/**
 * Choix de l'arrière-plan de la caméra (Réglages, écran d'accueil) : aucun,
 * flou léger ou fort, un décor, ou une image de son ordinateur (gardée dans
 * ce navigateur, jamais envoyée au serveur). Comme Meet : une grille de
 * vignettes, l'effet s'applique dès le clic. `compact` (écran d'accueil) :
 * les premiers décors seulement, les autres derrière « Voir tous les décors ».
 */
const COMPACT_PHOTOS = 5;

export function VideoBackgroundPicker({ compact = false }: { compact?: boolean }) {
  const raw = useGameStore((s) => s.settings.videoBackground);
  const updateSettings = useGameStore((s) => s.updateSettings);
  const value: VideoBackground = isVideoBackground(raw) ? raw : 'none';
  const [custom, setCustom] = useState<string | null>(() => loadCustomBackground());
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [expanded, setExpanded] = useState(false);
  // Décor choisi parmi les autres : la grille s'ouvre pour le montrer.
  const chosenIndex = BACKGROUND_PHOTOS.findIndex((p) => `photo:${p.id}` === value);
  const showAll = !compact || expanded || chosenIndex >= COMPACT_PHOTOS;
  const photos = showAll ? BACKGROUND_PHOTOS : BACKGROUND_PHOTOS.slice(0, COMPACT_PHOTOS);

  useEffect(() => onCustomBackgroundChange(() => setCustom(loadCustomBackground())), []);

  const choose = (v: VideoBackground) => { setError(null); updateSettings({ videoBackground: v }); };

  async function handleFile(file: File) {
    setError(null);
    try {
      await saveCustomBackground(file);
      updateSettings({ videoBackground: 'custom' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cette image n’a pas pu être utilisée.');
    }
  }

  function removeCustom() {
    clearCustomBackground();
    if (value === 'custom') updateSettings({ videoBackground: 'none' });
  }

  const tile = (v: VideoBackground, label: string, content: React.ReactNode, className = '') => (
    <button
      key={v}
      type="button"
      role="radio"
      aria-checked={value === v}
      aria-label={label}
      title={label}
      className={`pl-bg-tile ${className}`}
      onClick={() => choose(v)}
    >
      {content}
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span className="pl-field-label">Arrière-plan de la caméra</span>
      <div className="pl-bg-grid" role="radiogroup" aria-label="Arrière-plan de la caméra">
        {tile('none', 'Aucun', <><Icon name="ban" size={18} /><span>Aucun</span></>, 'pl-bg-tile-text')}
        {tile('blur-light', 'Flou léger', <span>Flou léger</span>, 'pl-bg-tile-text pl-bg-tile-blur-light')}
        {tile('blur', 'Flou fort', <span>Flou fort</span>, 'pl-bg-tile-text pl-bg-tile-blur')}
        {photos.map((p) => tile(`photo:${p.id}`, p.label, <img src={backgroundPhotoUrl(p.id, true)} alt="" loading="lazy" />))}
        {custom
          ? tile('custom', 'Mon image', <img src={custom} alt="" />)
          : (
            <button type="button" className="pl-bg-tile pl-bg-tile-text" aria-label="Choisir une image" title="Choisir une image" onClick={() => fileRef.current?.click()}>
              <Icon name="upload" size={18} /><span>Mon image</span>
            </button>
          )}
      </div>
      {!showAll && (
        <div>
          <Button variant="ghost" size="sm" onClick={() => setExpanded(true)}>
            Voir tous les décors ({BACKGROUND_PHOTOS.length})
          </Button>
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); e.target.value = ''; }}
      />
      {custom && (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button variant="ghost" size="sm" icon="upload" onClick={() => fileRef.current?.click()}>Changer mon image</Button>
          <Button variant="ghost" size="sm" icon="trash" onClick={removeCustom}>Retirer mon image</Button>
        </div>
      )}
      <p className="pl-help" style={{ margin: 0 }}>
        Les autres ne voient que toi devant l’arrière-plan choisi. Ton image reste dans ce navigateur.
      </p>
      {error && <p role="alert" style={{ margin: 0, color: 'var(--color-danger)', fontSize: 'var(--text-sm)' }}>{error}</p>}
    </div>
  );
}
